import * as React from 'react';
import * as yup from 'yup';
import {FormProvider, useForm} from 'react-hook-form';
import {useRef, useState} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';

import Button from '../../ui/Buttons/Button';
import {ContentModal} from '../../ui/modals/ContentModal/ContentModal';
import {CookiesService} from '../../../client/cookies/cookies.service';
import {GaItemListName} from '../../ga/ga.types';
import {Input} from '../../ui/forms/Input/Input';
import {Item, OrderableItem} from '../item.class';
import {ItemToAdd} from '../../order-items/order-items.class';
import {ProductImage} from '../../ui/ProductImage/ProductImage';
import {Select} from '../../ui/forms/Select/Select';
import {useService} from '../../react/ServiceContext';
import {usStatesOptions} from '../../ui/forms/DropDownHelper';

interface ShipRestrictionsFormValues {
    restrictionState: string;
    restrictionZip: string;
}

interface ShipRestrictionsModalProps {
    addToOrder: (itemsToAdd: ItemToAdd[], message?: string, increment?: boolean, useAddToOrderModal?: boolean) => void;
    gaItemListName: GaItemListName;
    onHide: () => void;
    orderableItem: OrderableItem | Item;
    show: boolean;
    unitsOrdered: number;
    useAddToOrderModal: boolean;
}

export const ShipRestrictionsModal = ({
    addToOrder,
    gaItemListName,
    onHide,
    orderableItem,
    show,
    unitsOrdered,
    useAddToOrderModal,
}: ShipRestrictionsModalProps) => {
    const [showCannotShipMessage, setShowCannotShipMessage] = useState(false);
    let initialState = '';
    let initialZip = '';
    const formRef = useRef(null);
    const formValidation = yup.object().shape({
        restrictionState: yup.string().required(`Please select a state`),
        restrictionZip: yup.string().required(`Please enter a Zip Code`),
    });
    const cookiesService: CookiesService = useService(`cookiesService`);
    const getGeoCookie = cookiesService?.getCookie('geo');
    const geo = getGeoCookie?.split('|');
    if (geo?.length >= 3) {
        initialState = geo[2];
        initialZip = geo[0];
    }

    const useFormReturn = useForm({
        defaultValues: {restrictionState: initialState, restrictionZip: initialZip},
        mode: `onBlur`,
        resolver: yupResolver(formValidation),
    });

    const submitForm = () => {
        if (formRef.current) {
            formRef.current.dispatchEvent(new Event(`submit`, {bubbles: true, cancelable: true}));
        }
    };

    /**
     * Confirms where item will be shipped before addToOrder
     * @param formValues
     */
    const confirmShipmentLocation = (formValues: ShipRestrictionsFormValues) => {
        if (orderableItem.excludeStArray.indexOf(formValues.restrictionState) >= 0) {
            setShowCannotShipMessage(true);
            return;
        }
        if (orderableItem.excludeStArray.indexOf(formValues.restrictionZip) >= 0) {
            setShowCannotShipMessage(true);
            return;
        }
        onHide();
        // Else invoke addToOrder()
        addToOrder(
            [{gaEcommerce: {item_list_name: gaItemListName}, item: orderableItem?.item, unitsOrdered}],
            null,
            true,
            useAddToOrderModal,
        );
    };

    /**
     * Template
     */
    return (
        <>
            <ContentModal
                footer={
                    <>
                        {!showCannotShipMessage && (
                            <Button
                                e2e="confirmShipmentLocation"
                                modal={true}
                                onClick={submitForm}
                                size="smToLg"
                                type="submit"
                                variant="secondary"
                            >
                                Continue
                            </Button>
                        )}
                    </>
                }
                onClose={onHide}
                show={show}
                title={
                    <>
                        {!showCannotShipMessage && (
                            <div className="tw-text-[1.75rem] tw-font-normal tw-leading-[1.2] tw-mb-0">Shipping Restrictions May Apply</div>
                        )}
                        {showCannotShipMessage && (
                            <div className="tw-text-[1.75rem] tw-font-normal tw-leading-[1.2] tw-mb-0">Unable to ship item</div>
                        )}
                    </>
                }
            >
                <>
                    {!showCannotShipMessage && <div>This product cannot be shipped to some parts of the United States and Canada.</div>}
                    {showCannotShipMessage && (
                        <div>
                            This product cannot be shipped to your location due to regulations in your area. Please call us at
                            1-800-558-2808 to discuss alternative products or shipping methods.
                            <br />
                            <br />
                            This item has not been added to your order.
                        </div>
                    )}
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-items-center tw-my-4">
                        <div className="tw-w-full tw-relative">
                            <ProductImage
                                altText={orderableItem?.description}
                                containerClass="tw-text-center"
                                size={120}
                                src={orderableItem?.imagePath}
                            />
                        </div>
                        <div className="tw-w-full tw-relative tw-col-span-1 md:tw-col-span-2">
                            <div dangerouslySetInnerHTML={{__html: orderableItem?.description}} />
                            <div>
                                Pkg Qty {orderableItem?.pkgQty} | Order Qty {unitsOrdered}
                            </div>
                        </div>
                    </div>
                    {!showCannotShipMessage && (
                        <FormProvider {...useFormReturn}>
                            <form
                                noValidate={true}
                                onSubmit={(event) => {
                                    useFormReturn
                                        .handleSubmit(confirmShipmentLocation)(event)
                                        .catch((handleSubmitErr) => {
                                            useFormReturn.setError(`restrictionZip`, {message: handleSubmitErr.message});
                                        });
                                }}
                                ref={formRef}
                            >
                                <div>Where will this product be shipped?</div>
                                <Select
                                    className="maskPII"
                                    error={useFormReturn?.formState?.errors?.restrictionState?.message}
                                    id="shipToState"
                                    label="State"
                                    name="restrictionState"
                                    options={usStatesOptions()}
                                    required
                                    {...useFormReturn.register(`restrictionState`)}
                                />
                                <Input
                                    className="maskPII"
                                    error={useFormReturn?.formState?.errors?.restrictionZip?.message}
                                    label="Zip Code"
                                    maxLength={5}
                                    name="restrictionZip"
                                    numbersOnly={true}
                                    required
                                    {...useFormReturn.register('restrictionZip')}
                                />
                            </form>
                        </FormProvider>
                    )}
                </>
            </ContentModal>
        </>
    );
};
